import React from 'react';

import { Alert, Select, Space, Spin, Switch, Tag, Typography } from 'antd';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { GeoNode } from '@app/models/dictionaries';
import { isForbiddenError } from '@app/services/apiErrors';

import { ATTR_META, GEO_SEARCH_MIN_LENGTH } from '@pages/Employees/data/constants';
import { useGeoLabels, useGeoLocalitySuggestQuery, useGeoRegionSuggestQuery } from '@pages/Employees/hooks';
import { IncludeExclude, Mode, RuleAttribute, RuleValue } from '@pages/Employees/interfaces';
import { geoNodeLabel } from '@pages/Employees/services';

import { AttrLabel, AttrRow, ChipsRow } from './styles';

interface IProps {
  lists: IncludeExclude;
  onChange: (lists: IncludeExclude) => void;
}

export const GeoAttributeControl: React.FC<IProps> = ({ lists, onChange }) => {
  const assignmentsT = useTranslation(NSKeys.assignments);
  const [mode, setMode] = React.useState<Mode>('include');
  const [region, setRegion] = React.useState<GeoNode | undefined>(undefined);
  const [regionSearch, setRegionSearch] = React.useState('');
  const [regionQuery, setRegionQuery] = React.useState('');
  const [localitySearch, setLocalitySearch] = React.useState('');
  const [localityQuery, setLocalityQuery] = React.useState('');
  const [picked, setPicked] = React.useState<Record<string, string>>({});

  const debouncedRegionQuery = React.useMemo(() => debounce(setRegionQuery, 400), []);
  const debouncedLocalityQuery = React.useMemo(() => debounce(setLocalityQuery, 400), []);

  React.useEffect(
    () => () => {
      debouncedRegionQuery.cancel();
      debouncedLocalityQuery.cancel();
    },
    [debouncedRegionQuery, debouncedLocalityQuery]
  );

  const regions = useGeoRegionSuggestQuery(regionQuery);
  const localities = useGeoLocalitySuggestQuery(region?.fias_id, localityQuery);
  const searchError = regions.error || localities.error;
  const isSearchForbidden = !!searchError && isForbiddenError(searchError);
  const searchErrorText = searchError
    ? isSearchForbidden
      ? assignmentsT.t('geo.forbidden')
      : assignmentsT.t('geo.unavailable')
    : undefined;

  const codes = React.useMemo(
    () => [...lists.include, ...lists.exclude].filter((v) => !picked[String(v)]),
    [lists.include, lists.exclude, picked]
  );
  const geo = useGeoLabels(codes);
  const geoLabel = (fias: RuleValue) => picked[String(fias)] || geo.labelOf(fias);

  const localityNodes = [...(region ? [region] : []), ...(localities.data || [])];
  const localityOptions = localityNodes.map((node) => ({
    label: node === region ? `${assignmentsT.t('geo.wholeRegion')}: ${node.name}` : node.name,
    value: node.fias_id,
  }));
  const selectedLocalities = lists[mode].map(String).filter((v) => localityNodes.some((node) => node.fias_id === v));

  const handleRegionChange = (value?: string) => {
    setRegion((regions.data || []).find((node) => node.fias_id === value));
    setLocalitySearch('');
    debouncedLocalityQuery.cancel();
    setLocalityQuery('');
  };

  const handleLocalitiesChange = (values: string[]) => {
    const added = localityNodes.filter((node) => values.includes(node.fias_id));
    setPicked((prev) => ({ ...prev, ...Object.fromEntries(added.map((node) => [node.fias_id, geoNodeLabel(node)])) }));
    const kept = lists[mode].filter((v) => !localityNodes.some((node) => node.fias_id === String(v)));
    onChange({ ...lists, [mode]: [...kept, ...values] });
  };

  const handleRemove = (list: Mode, value: RuleValue) => {
    onChange({ ...lists, [list]: lists[list].filter((x) => String(x) !== String(value)) });
  };

  const notFoundContent = (search: string, isFetching: boolean, hint: string) => {
    if (search.trim().length < GEO_SEARCH_MIN_LENGTH) {
      return hint;
    }
    if (isFetching) {
      return <Spin size="small" />;
    }
    if (searchError) {
      return isSearchForbidden ? assignmentsT.t('geo.forbiddenShort') : assignmentsT.t('geo.unavailable');
    }
    return assignmentsT.t('select.empty');
  };

  const hasChips = lists.include.length > 0 || lists.exclude.length > 0;

  return (
    <AttrRow>
      <AttrLabel>{assignmentsT.t(ATTR_META[RuleAttribute.RecipientGeo].labelKey as any)}</AttrLabel>
      <Switch
        checked={mode === 'include'}
        checkedChildren={assignmentsT.t('toggle.include')}
        unCheckedChildren={assignmentsT.t('toggle.exclude')}
        onChange={(checked) => setMode(checked ? 'include' : 'exclude')}
      />
      <Space.Compact style={{ flex: 1, minWidth: 320 }}>
        <Select
          style={{ width: '45%' }}
          showSearch={{
            searchValue: regionSearch,
            autoClearSearchValue: false,
            filterOption: false,
            onSearch: (v) => {
              setRegionSearch(v);
              debouncedRegionQuery(v);
            },
          }}
          allowClear
          loading={regions.isFetching}
          value={region?.fias_id}
          placeholder={assignmentsT.t('geo.regionPlaceholder')}
          options={(regions.data || []).map((node) => ({ label: node.name, value: node.fias_id }))}
          onChange={handleRegionChange}
          notFoundContent={notFoundContent(regionSearch, regions.isFetching, assignmentsT.t('geo.typeRegion'))}
        />
        <Select
          style={{ width: '55%' }}
          mode="multiple"
          disabled={!region}
          showSearch={{
            searchValue: localitySearch,
            autoClearSearchValue: false,
            filterOption: false,
            onSearch: (v) => {
              setLocalitySearch(v);
              debouncedLocalityQuery(v);
            },
          }}
          loading={localities.isFetching}
          value={selectedLocalities}
          placeholder={assignmentsT.t('geo.localityPlaceholder')}
          options={localityOptions}
          onChange={handleLocalitiesChange}
          notFoundContent={
            region
              ? notFoundContent(localitySearch, localities.isFetching, assignmentsT.t('geo.typeLocality'))
              : undefined
          }
        />
      </Space.Compact>
      {searchErrorText && <Alert type="warning" showIcon title={searchErrorText} style={{ flexBasis: '100%' }} />}
      {hasChips && (
        <ChipsRow style={{ flexBasis: '100%' }}>
          {lists.include.length > 0 && (
            <>
              <Typography.Text type="secondary">{assignmentsT.t('toggle.include')}:</Typography.Text>
              {lists.include.map((v) => (
                <Tag key={`i-${v}`} closable color="blue" onClose={() => handleRemove('include', v)}>
                  {geoLabel(v)}
                </Tag>
              ))}
            </>
          )}
          {lists.exclude.length > 0 && (
            <>
              <Typography.Text type="secondary">{assignmentsT.t('toggle.exclude')}:</Typography.Text>
              {lists.exclude.map((v) => (
                <Tag key={`e-${v}`} closable color="red" onClose={() => handleRemove('exclude', v)}>
                  {geoLabel(v)}
                </Tag>
              ))}
            </>
          )}
        </ChipsRow>
      )}
    </AttrRow>
  );
};
